import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { match } from 'ts-pattern';
import noop from 'utils/noop';
import { ClientDataType } from 'modules/queryBuilder/types';
import { spacing, body2, body2Bold, colors, caption } from 'ui/theme';
import { FlexSpacer } from 'ui/flexSpacer';
import { TextButton } from 'ui/button';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer/utils';
import { useUpdateGoalVisibilityMutation } from 'modules/missionControl/hooks/useUpdateGoalVisibilityMutation';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { Goal, GoalEventType } from 'modules/goals/types';
import { BasicOptionProps } from 'ui/selects/types';
import { Custom24 } from 'ui/icons/aurora/24px/userEvents/Custom24';
import { Purchase24 } from 'ui/icons/aurora/24px/userEvents/Purchase24';
import { TruncateTooltip } from 'ui/utility/truncate/TruncateTooltip';
import { ToastMessage } from 'modules/goals/lib/components/ToastMessage';
import { useIsOverflowing } from 'utils/hooks/useIsOverflowing';
import { pxToRem } from 'utils/convert';
import { DATA_TYPE_TO_ICON } from './AddGoalSelect.constants';
import { GOAL_CARD_TEST_IDS } from '../GoalCard/GoalCard.constants';

const MAX_LABEL_CONTENT_WIDTH = 360;

interface AddGoalSelectOptionProps extends PropsWithChildren<BasicOptionProps> {
  data: Goal;
  label: string;
  id: string;
}

const Option = styled.div`
  display: grid;
  grid-template-columns: ${pxToRem(24)} ${pxToRem(MAX_LABEL_CONTENT_WIDTH)} auto;
  gap: ${spacing.half};
  padding: ${spacing.threeQuarter} ${spacing.half};
`;

const OptionLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

const GoalName = styled.span`
  ${body2Bold}
  display: flex;
`;

const EventName = styled.span`
  ${body2}
  color: ${colors.textSecondary};
  flex-shrink: 0;
`;

const CreatedBy = styled.span`
  ${caption}
  color: ${colors.textSecondary};
`;

const AddButton = styled(TextButton)`
  padding: 0;
`;

export const AddGoalSelectOption = (props: AddGoalSelectOptionProps) => {
  const { data: goal, label, id, children, ...restProps } = props;
  const { elementRef, maxViewableWidth: maxViewableEventNameWidth } =
    useIsOverflowing<HTMLDivElement>();

  const maxGoalNameWidth = MAX_LABEL_CONTENT_WIDTH - maxViewableEventNameWidth - 8; // 8px is the gap between the event name and the goal name

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    missionControlUserPreferencesQuery: { refetch: refetchMissionControlUserPreferences },
    goalCreatorsQuery: { goalCreators },
  } = useMissionControlContext();

  const { mutate: updateGoalVisibility } = useUpdateGoalVisibilityMutation({
    onSuccess: () => {
      dispatch(
        addPositiveToast(<ToastMessage name={goal.name} suffix=" goal added to homepage." />),
      );
      refetchMissionControlUserPreferences(); // NOTE: we will just use the response from `useUpdateGoalVisibilityMutation` in the future, instead of refetching the user preferences.
    },
    onError: () => {
      dispatch(
        addNegativeToast(
          <ToastMessage
            prefix="Unable to add "
            name={goal.name}
            suffix=" goal to homepage. Please try again."
          />,
        ),
      );
    },
  });
  const fullName = goalCreators?.find((creator) => creator.id === goal.createdBy)?.fullName;

  const icon = match(goal.eventType)
    .with(GoalEventType.CustomEvent, () => (
      <Custom24 data-test={GOAL_CARD_TEST_IDS.GOAL_CUSTOM_EVENT_ICON} />
    ))
    .with(GoalEventType.Purchase, () => (
      <Purchase24 data-test={GOAL_CARD_TEST_IDS.GOAL_CUSTOM_EVENT_ICON} />
    ))
    .otherwise(() => DATA_TYPE_TO_ICON[goal.eventName as ClientDataType]);

  const eventName = match(goal.eventType)
    .with(
      GoalEventType.System,
      () => t(`v2.requirementBuilder.eventName.${goal.eventName}`) || goal.eventName,
    )
    .otherwise(() => goal.eventName);

  return (
    <Option
      id={id}
      {...restProps}
      /**
       * NOTE: this prevent the default behavior of the option from being clicked,
       * otherwise the select will close.
       */
      onClick={noop}
      onKeyDown={noop}
    >
      <FlexSpacer alignItems={'center'} justifyContent={'center'}>
        {icon}
      </FlexSpacer>
      <OptionLabel>
        <FlexSpacer alignItems={'center'} gap={spacing.half}>
          <GoalName>
            <TruncateTooltip textOnly name={label} maxWidth={maxGoalNameWidth} />
          </GoalName>
          <EventName ref={elementRef}>{eventName}</EventName>
        </FlexSpacer>
        <div>
          <CreatedBy>Created by {fullName || goal.createdBy}</CreatedBy>
        </div>
      </OptionLabel>
      <FlexSpacer justifyContent={'flex-end'}>
        <AddButton
          onClick={() => {
            updateGoalVisibility({
              goalId: goal.id,
              visibleToCurrentUser: true,
            });
          }}
        >
          Add
        </AddButton>
      </FlexSpacer>
    </Option>
  );
};
